import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Pagination,
  Chip,
  Tooltip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { getPatientProfileRequest, deletePatientProfileRequest, resetDelete, resetPatientData, createPatientAccountRequest, resetCreateAccountRes } from '../../../redux/modules/admin/patientProfile/slice';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { toast } from 'react-toastify';
import { DeleteConfirmationModal } from '../../../utils/modal';
import PatientTabHeader from './patientTabHeader';
import moment from 'moment';

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    padding: '14px 16px',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& .MuiTableRow-root': {
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '& .MuiTableCell-body': {
    padding: '14px 16px',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const GenderChip = styled(Chip)(({ theme, gendertype }) => {
  const colors = {
    male: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' },
    female: { bg: 'rgba(236, 72, 153, 0.1)', color: '#EC4899' },
    other: { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280' },
  };
  const colorSet = colors[gendertype?.toLowerCase()] || colors.other;
  return {
    backgroundColor: colorSet.bg,
    color: colorSet.color,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24,
    textTransform: 'capitalize',
  };
});

const ActionButton = styled(IconButton)(({ theme, color }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  marginRight: 4,
  backgroundColor: color === 'error'
    ? 'rgba(239, 68, 68, 0.08)'
    : 'rgba(105, 203, 249, 0.08)',
  '&:hover': {
    backgroundColor: color === 'error'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(105, 203, 249, 0.15)',
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  color: theme.palette.text.secondary,
}));

const PaginationWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  '& .MuiPagination-ul': {
    '& .MuiPaginationItem-root': {
      borderRadius: 8,
      fontWeight: 500,
    },
  },
}));

const tableHeading = ["Full Name", "Patient ID", "Email Address", "Phone Number", "Date of Birth", "Gender", "Action"];

let deleteItemId;

export default function PatientList({ setItemData, toggleList }) {
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("");
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  let { getInfoRes, deleteRes, loading, createAccountRes, createAccountLoading } = useSelector(state => state.patientProfile);
  let { currentBranch } = useSelector(state => state.branch);
  const [isModalOpen, setIsModalOpen] = useState(false);
  let cardLimit = 10;
  let pageCount = 0;

  if (getInfoRes?.dataCount && getInfoRes?.dataCount > cardLimit) {
    pageCount = getInfoRes.dataCount / cardLimit;
    if (pageCount % 2 !== 0) {
      pageCount = Math.ceil(pageCount);
    }
  }

  useEffect(() => {
    if (!currentBranch) { return; }
    dispatch(getPatientProfileRequest({
      limit: cardLimit, search: searchText, searchType: searchType, skip: (page - 1) * cardLimit,
      currentBranch: currentBranch?.id,
    }));
    dispatch(resetPatientData());
    localStorage.removeItem("pId");
  }, [dispatch, currentBranch, searchText, searchType, cardLimit, page]);

  useEffect(() => {
    if (!deleteRes) { return; }
    if (deleteRes?.success) {
      dispatch(getPatientProfileRequest({
        limit: cardLimit, search: searchText, searchType: searchType, skip: (page - 1) * cardLimit,
        currentBranch: currentBranch?.id,
      }));
    }
    toast(deleteRes?.message);
    dispatch(resetDelete());
  }, [dispatch, deleteRes, currentBranch, cardLimit, page, searchText, searchType]);

  useEffect(() => {
    if (!createAccountRes) { return; }
    toast(createAccountRes?.message);
    dispatch(resetCreateAccountRes());
  }, [dispatch, createAccountRes]);

  const handleCreateAccount = (patientId) => {
    dispatch(createPatientAccountRequest(patientId));
  };

  const handleSearchCallback = (type, text) => {
    setSearchType(type);
    setSearchText(text);
    setPage(1);
  };

  const handlePagination = (event, value) => {
    setPage(value);
  };

  // Loading State
  const renderSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          {tableHeading.map((_, cellIndex) => (
            <TableCell key={cellIndex}>
              <Skeleton variant="text" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  return (
    <>
      <PatientTabHeader handleSearchCallback={handleSearchCallback} />

      <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
      <StyledTableContainer component={Paper}>
        <Table sx={{ minWidth: { xs: 600, md: 700 } }} aria-label="patient table">
          <StyledTableHead>
            <TableRow>
              {tableHeading.map(elem => (
                <TableCell key={elem}>{elem}</TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <StyledTableBody>
            {loading ? (
              renderSkeleton()
            ) : getInfoRes?.data?.length > 0 ? (
              getInfoRes.data.map(elem => (
                <TableRow key={elem._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: 'primary.light',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.dark',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      >
                        {elem.fName?.[0]?.toUpperCase()}{elem.lName?.[0]?.toUpperCase()}
                      </Box>
                      <Typography fontWeight={500}>
                        {elem.fName} {elem.lName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {elem?.mrn || elem?.orthancPatientId || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {elem?.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {elem?.contactNumber || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {elem?.dob ? moment(elem?.dob).format('MMM DD, YYYY') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {elem?.gender && (
                      <GenderChip
                        label={elem.gender}
                        gendertype={elem.gender}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {elem?.email && (
                        <Tooltip title="Create Patient Account">
                          <ActionButton
                            size="small"
                            onClick={() => handleCreateAccount(elem._id)}
                            disabled={createAccountLoading}
                            sx={{ backgroundColor: 'rgba(46, 125, 50, 0.08)', '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.15)' } }}
                          >
                            <PersonAddIcon fontSize="small" sx={{ color: 'success.main' }} />
                          </ActionButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit Patient">
                        <ActionButton
                          size="small"
                          onClick={() => { setItemData(elem); toggleList(); }}
                        >
                          <EditIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        </ActionButton>
                      </Tooltip>
                      <Tooltip title="Delete Patient">
                        <ActionButton
                          size="small"
                          color="error"
                          onClick={() => { deleteItemId = elem._id; setIsModalOpen(true); }}
                        >
                          <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                        </ActionButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeading.length}>
                  <EmptyState>
                    <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                    <Typography variant="h6" fontWeight={500} color="text.secondary">
                      No Patients Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchText ? 'Try adjusting your search criteria' : 'Add patients to get started'}
                    </Typography>
                  </EmptyState>
                </TableCell>
              </TableRow>
            )}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>
      </Box>

      {pageCount > 1 && (
        <PaginationWrapper>
          <Pagination
            count={pageCount}
            color="primary"
            shape="rounded"
            page={page}
            onChange={handlePagination}
          />
        </PaginationWrapper>
      )}

      <DeleteConfirmationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => { dispatch(deletePatientProfileRequest(deleteItemId)); setIsModalOpen(false); }}
      />
    </>
  );
}
